import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useActionData, useNavigate, useParams } from 'react-router-dom'

import { Breadcrumb } from '@/components/Breadcrumb.tsx'
import { StyledActionButton } from '@/components/Buttons.tsx'
import { NotificationWarning } from '@/components/Notification.tsx'
import { FormStyled, FormSubmit } from '@/components/templates/Form.tsx'
import { FormField } from '@/components/templates/FormField.tsx'
import { ScrollableContent } from '@/components/templates/ScreenLayout.tsx'
import { ScreenWrapper } from '@/components/templates/ScreenWrapper.tsx'
import { Subheader } from '@/components/Text.tsx'
import { useTrackFormProgress } from '@/hooks/useTrackFormProgress.ts'
import { Header } from '@/screens/velthuis/BMICalculator/BMICalculator.styled.tsx'
import { useAppointmentStore } from '@/store.ts'
import { storageInsuranceNoticeDismissed } from '@/utils/sessionStorage.ts'
import { VelthuisFormStep } from '@/utils/VelthuisFormSteps.ts'

import { ActionData } from './actions.ts'

// Timeff codes of the only treatments that can be covered by health insurance.
// Note: CON_PC_G_OOL (lower eyelid) is deliberately absent — not covered.
const coveredTreatments = ['CON_PC_B_VERK', 'CON_PC_G_BOL', 'CON_PC_L_BUIKW']

export const BMICalculator = () => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'bmiCalculator',
  })
  const navigate = useNavigate()
  const actionData = useActionData() as ActionData | undefined
  const { appointmentType } = useParams()
  const [showInsuranceNotice, setShowInsuranceNotice] = useState(
    () =>
      !!appointmentType &&
      coveredTreatments.includes(appointmentType) &&
      !storageInsuranceNoticeDismissed.get(),
  )
  const { bmiData } = useAppointmentStore((state) => ({
    bmiData: state.bmiData,
  }))
  const { trackFormInteraction } = useTrackFormProgress(
    VelthuisFormStep.MoreInformation,
  )

  return (
    <ScreenWrapper>
      <Breadcrumb
        onClick={() => {
          navigate(-1)
          trackFormInteraction({
            action: 'previous',
            formFieldName: 'back-to-previous',
          })
        }}
        title={t('breadcrumbLink')}
      />

      <FormStyled method="post">
        <Header>{t('header')}</Header>
        <Subheader>{t('subheader')}</Subheader>
        <ScrollableContent>
          <FormField
            fieldName="height"
            fieldType="number"
            defaultValue={bmiData?.height}
            trackFormInteraction={trackFormInteraction}
            allErrors={actionData?.errors}
          />
          <FormField
            fieldName="weight"
            fieldType="number"
            defaultValue={bmiData?.weight}
            trackFormInteraction={trackFormInteraction}
            allErrors={actionData?.errors}
          />
        </ScrollableContent>
        {showInsuranceNotice && (
          <NotificationWarning
            label={t('insuranceNotice', {
              treatment: t(`coveredTreatments.${appointmentType}`),
            })}
            onClose={() => {
              storageInsuranceNoticeDismissed.set()
              setShowInsuranceNotice(false)
            }}
          />
        )}
        <FormSubmit>
          <StyledActionButton
            variant="Primary"
            type="submit"
            onClick={() => {
              trackFormInteraction({
                action: 'next',
                formFieldName: 'submit',
              })
            }}
          >
            {t('actions.continue')}
          </StyledActionButton>
        </FormSubmit>
      </FormStyled>
    </ScreenWrapper>
  )
}
